<template>
  <div id="natcon-payment-create">
    <v-overlay :value="ProgressLoadingFlag">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Hero Header Section -->
    <v-card class="hero-card elevation-0 mb-6 animate-fade-in">
      <div class="hero-gradient-jci">
        <v-container>
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8" class="py-8">
              <div class="d-flex align-center animate-slide-right">
                <div class="hero-icon-box-jci mr-4">
                  <v-icon size="64" color="#EDBE38" class="hero-icon-animated">mdi-calendar-account</v-icon>
                </div>
                <div>
                  <h1 class="hero-title-jci white--text mb-2">
                    {{ PageTitle }}
                  </h1>
                  <p class="hero-subtitle-jci white--text mb-0">
                    {{ PageDescription }}
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="py-8 text-md-right text-center animate-slide-left">
              <v-btn
                @click.prevent="$router.go(-1)"
                color="#56BDA3"
                large
                class="hero-action-btn-jci mr-3"
              >
                <v-icon left color="white">mdi-arrow-left</v-icon>
                <span style="color: white; font-weight: 600;">BACK</span>
              </v-btn>
              <v-btn
                @click.prevent="refreshPageData"
                color="#EDBE38"
                large
                class="hero-action-btn-jci"
              >
                <v-icon left color="#0A0F29">mdi-refresh</v-icon>
                <span style="color: #0A0F29; font-weight: 600;">REFRESH</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <v-container>
      <!-- Important Notes Alert -->
      <v-alert
        v-if="NotesFlag"
        border="left"
        colored-border
        color="#0097D7"
        elevation="2"
        class="mb-6 jci-alert animate-fade-in"
        prominent
      >
        <div class="d-flex align-start">
          <v-icon large class="mr-3" color="#0097D7">mdi-information-outline</v-icon>
          <div>
            <h3 class="mb-3" style="color: #0A0F29;">Important Information</h3>
            <ol class="pl-4">
              <li class="mb-2">Regular subscription can be paid only once.</li>
              <li class="mb-2">
                After paying regular subscription, choose additional
                subscription.
              </li>
              <li class="mb-2">
                Late fee is optional but paying late fee is required to get
                voting rights.
              </li>
              <li class="mb-2">
                After completing the payment, kindly select your members for
                whom the payment was made.
              </li>
            </ol>
          </div>
        </div>
      </v-alert>

      <!-- Main Content Card -->
      <v-card class="main-card-jci elevation-4 animate-scale-up" rounded="lg">
        <v-card-text class="pa-6">
          <div class="required-note-jci mb-6">
            <v-icon small color="#EDBE38">mdi-asterisk</v-icon>
            <span class="ml-1 text-caption" style="color: #414042;">indicates required field</span>
          </div>

          <!-- Loading State -->
          <div class="d-flex justify-center mb-6" v-if="LoadingFlag">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
          </div>

          <!-- Skeleton Loader -->
          <v-container v-if="StartupLoadingFlag">
            <v-row>
              <v-col cols="12" md="4" v-for="n in 6" :key="n">
                <v-skeleton-loader
                  type="article"
                  elevation="2"
                ></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-container>

          <!-- Main Form Content -->
          <v-container v-if="!StartupLoadingFlag" class="px-0">
            <!-- Action Buttons Section -->
            <v-row v-if="RefreshButtonFlag" class="mb-6">
              <v-col cols="12" class="text-center">
                <v-btn
                  @click.prevent="refreshPageData"
                  color="#0097D7"
                  elevation="3"
                  rounded
                  x-large
                  class="jci-btn jci-btn-aqua mx-2 mb-3"
                >
                  <v-icon left color="white">mdi-refresh</v-icon>
                  <span style="color: white;">Refresh</span>
                </v-btn>
                <v-btn
                  @click.prevent="resetForm"
                  color="#414042"
                  elevation="3"
                  rounded
                  x-large
                  class="jci-btn jci-btn-dark mx-2 mb-3"
                >
                  <v-icon left color="white">mdi-close-circle</v-icon>
                  <span style="color: white;">Clear</span>
                </v-btn>
                <router-link
                  to="/natcon/non-member-payment/create"
                  style="text-decoration: none"
                >
                  <v-btn
                    color="#56BDA3"
                    elevation="3"
                    rounded
                    x-large
                    class="jci-btn jci-btn-seafoam mx-2 mb-3"
                  >
                    <v-icon left color="white">mdi-account-plus</v-icon>
                    <span style="color: white;">Non Member Registration</span>
                  </v-btn>
                </router-link>
              </v-col>
            </v-row>

            <!-- Registration Form -->
            <v-form ref="form2" v-model="valid1" lazy-validation>
              <!-- Registration Type and Member Selection Card -->
              <v-card
                class="info-card-jci mb-6 animate-slide-up"
                elevation="3"
                rounded="lg"
                v-if="RegistrationTypeFlag"
              >
                <v-card-title class="card-header-jci-navy white--text">
                  <v-icon left color="white">mdi-clipboard-text</v-icon>
                  Registration Details
                </v-card-title>
                <v-card-text class="pa-6">
                  <v-row>
                    <v-col cols="12" md="4">
                      <label class="field-label">
                        <span class="error--text">*</span> Register for
                      </label>
                      <v-select
                        :reduce="(option) => option.value"
                        :items="RegistrationTypeIdOptions"
                        :rules="RegistrationTypeIdRules"
                        v-model="RegistrationTypeId"
                        required
                        outlined
                        dense
                        prepend-inner-icon="mdi-account-check"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="8" v-if="RegistrationTypeId >= 2">
                      <label class="field-label">
                        <span class="error--text">*</span> Select Member
                      </label>
                      <v-autocomplete
                        @change="getMemberDetails"
                        :reduce="(option) => option.value"
                        :loading="MemberIdOptionsLoading"
                        :items="MemberIdOptions"
                        :rules="MemberIdRules"
                        v-model="MemberId"
                        required
                        outlined
                        dense
                        clearable
                        prepend-inner-icon="mdi-account-search"
                      >
                        <template v-slot:selection="{ item }">
                          <v-avatar size="40" class="mr-2">
                            <img :src="item.photo" :alt="item.text" />
                          </v-avatar>
                          {{ item.text }}
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-avatar>
                            <img :src="item.photo" :alt="item.text" />
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              item.text
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- LOM and Member Information Cards -->
              <v-row class="mb-6">
                <!-- LOM Details Card -->
                <v-col cols="12" md="4">
                  <v-card class="info-card-jci h-100 animate-slide-up" elevation="3" rounded="lg">
                    <v-card-title class="card-header-jci-seafoam white--text">
                      <v-icon left color="white">mdi-domain</v-icon>
                      LO Information
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <div class="info-item">
                        <v-icon small color="primary" class="mr-2"
                          >mdi-office-building</v-icon
                        >
                        <span class="font-weight-bold">{{ Lom.LomName }}</span>
                      </div>
                      <v-divider class="my-3"></v-divider>
                      <div class="info-item">
                        <v-icon small color="primary" class="mr-2"
                          >mdi-map-marker</v-icon
                        >
                        <span>{{ Lom.ZoneName }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- President Details Card -->
                <v-col cols="12" md="4">
                  <v-card class="info-card-jci h-100 animate-slide-up" elevation="3" rounded="lg" style="animation-delay: 0.1s;">
                    <v-card-title class="card-header-jci-aqua white--text">
                      <v-icon left color="white">mdi-account-star</v-icon>
                      President Details
                    </v-card-title>
                    <v-card-text class="pa-4 text-center">
                      <v-avatar size="80" class="mb-3 elevation-3">
                        <v-img
                          :src="Presidentmember.MemberImagePath"
                          :alt="Presidentmember.LomPresidentFullName"
                          lazy-src="/no-image-available.png"
                        >
                          <template v-slot:placeholder>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <v-progress-circular
                                indeterminate
                                color="grey lighten-5"
                              ></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                      </v-avatar>
                      <div class="font-weight-bold mb-2">
                        {{ Presidentmember.LomPresidentFullName }}
                      </div>
                      <v-chip small color="primary" text-color="white">
                        {{ Presidentmember.LomPresidentMembershipId }}
                      </v-chip>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Selected Participant Card -->
                <v-col
                  cols="12"
                  md="4"
                  v-if="RegistrationTypeId != '' && ParticipantShowFlag"
                >
                  <v-card class="info-card-jci h-100 animate-slide-up" elevation="3" rounded="lg" style="animation-delay: 0.2s;">
                    <v-card-title class="card-header-jci-gold">
                      <v-icon left color="#0A0F29">mdi-account-circle</v-icon>
                      <span style="color: #0A0F29;">Selected Participant</span>
                    </v-card-title>
                    <v-card-text class="pa-4 text-center">
                      <v-avatar
                        v-if="selectedMember.MemberImagePath != ''"
                        size="80"
                        class="mb-3 elevation-3"
                      >
                        <v-img
                          :src="selectedMember.MemberImagePath"
                          :alt="selectedMember.MemberName"
                          lazy-src="/no-image-available.png"
                        >
                          <template v-slot:placeholder>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <v-progress-circular
                                indeterminate
                                color="grey lighten-5"
                              ></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                      </v-avatar>
                      <div class="font-weight-bold mb-2">
                        {{ selectedMember.MemberName }}
                      </div>
                      <v-chip small color="warning" text-color="white">
                        {{ selectedMember.MembershipId }}
                      </v-chip>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card class="info-card-jci mb-6 animate-slide-up" elevation="3" rounded="lg" style="animation-delay: 0.3s;">
                    <v-card-title class="card-header-jci-navy white--text">
                      <v-icon left color="white">mdi-phone-message</v-icon>
                      Contact Information
                    </v-card-title>
                    <v-card-text class="pa-6">
                      <v-row>
                        <v-col cols="12">
                          <label class="field-label">
                            <span class="error--text">*</span> Mobile Number
                          </label>
                          <v-text-field
                            v-model="selectedMember.MobileNo"
                            :rules="MobileNoRules"
                            :counter="10"
                            v-mask="'##########'"
                            type="number"
                            placeholder="Enter mobile number"
                            dense
                            outlined
                            prepend-inner-icon="mdi-cellphone"
                          ></v-text-field>
                        </v-col>

                        <v-col cols="12">
                          <label class="field-label">
                            <span class="error--text">*</span> Email Address
                          </label>
                          <v-text-field
                            v-model="selectedMember.EmailId"
                            :rules="EmailIdRules"
                            type="email"
                            placeholder="Enter email address"
                            dense
                            outlined
                            prepend-inner-icon="mdi-email"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Additional Details Card -->
              <v-card class="info-card-jci mb-6 animate-slide-up" elevation="3" rounded="lg">
                <v-card-title class="card-header-jci-aqua white--text">
                  <v-icon left color="white">mdi-account-details</v-icon>
                  Additional Details
                </v-card-title>
                <v-card-text class="pa-6">
                  <v-row>
                    <v-col cols="12" sm="6" md="3">
                      <label class="field-label">
                        <span class="error--text">*</span> Gender
                      </label>
                      <v-select
                        v-model="selectedMember.Gender"
                        :items="GenderOptions"
                        :rules="GenderRules"
                        outlined
                        dense
                        prepend-inner-icon="mdi-gender-male-female"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <label class="field-label">
                        <span class="error--text">*</span> Food preference
                      </label>
                      <v-select
                        v-model="selectedMember.FoodChoice"
                        :items="FoodPreferenceOptions"
                        :rules="FoodPreferenceRules"
                        outlined
                        dense
                        prepend-inner-icon="mdi-food"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <label class="field-label">
                        <span class="error--text">*</span> T-shirt Size
                      </label>
                      <v-select
                        v-model="TshirtSize"
                        :items="TshirtSizeOptions"
                        :rules="TshirtSizeRules"
                        outlined
                        dense
                        prepend-inner-icon="mdi-tshirt-crew"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <label class="field-label">
                        Senator number
                        <span class="text-caption">(optional)</span>
                      </label>
                      <v-text-field
                        v-model="SenatorNumber"
                        :rules="SenatorNumberRules"
                        placeholder="Enter senator number"
                        dense
                        outlined
                        prepend-inner-icon="mdi-account-star"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="3" v-if="GstFlag">
                      <label class="field-label">
                        Do you want GST Bill
                        <span class="text-caption">(optional)</span>
                      </label>
                      <v-select
                        v-model="selectedMember.GstNumberFlag"
                        :items="GstNumberFlagOptions"
                        outlined
                        dense
                        prepend-inner-icon="mdi-receipt"
                      ></v-select>
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                      md="3"
                      v-if="selectedMember.GstNumberFlag == 1"
                    >
                      <label class="field-label">
                        <span class="error--text">*</span> GST Number
                      </label>
                      <v-text-field
                        v-model="selectedMember.GstNumber"
                        :rules="GstNumberRules"
                        placeholder="Enter GST number"
                        dense
                        outlined
                        prepend-inner-icon="mdi-file-document"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Professional & Medical Information Card -->
              <v-card class="info-card-jci mb-6 animate-slide-up" elevation="3" rounded="lg">
                <v-card-title class="card-header-jci-seafoam white--text">
                  <v-icon left color="white">mdi-briefcase-account</v-icon>
                  Professional & Medical Information
                </v-card-title>
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Professional Information -->
                    <v-col cols="12">
                      <div class="section-subtitle-jci mb-3">
                        <v-icon small color="#56BDA3" class="mr-2"
                          >mdi-office-building</v-icon
                        >
                        <span class="font-weight-bold text-subtitle-2" style="color: #0A0F29;"
                          >Professional Details</span
                        >
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <label class="field-label">
                        <span class="error--text">*</span>
                        Company/Institution/Organization Name
                      </label>
                      <v-text-field
                        v-model="CompanyName"
                        :rules="CompanyNameRules"
                        placeholder="Enter company name"
                        dense
                        outlined
                        required
                        prepend-inner-icon="mdi-domain"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <label class="field-label">
                        <span class="error--text">*</span>
                        Section/Division/Department
                      </label>
                      <v-text-field
                        v-model="Department"
                        :rules="DepartmentRules"
                        placeholder="Enter department"
                        dense
                        outlined
                        required
                        prepend-inner-icon="mdi-office-building-outline"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                      <label class="field-label">
                        <span class="error--text">*</span> Job Title
                      </label>
                      <v-text-field
                        v-model="JobTitle"
                        :rules="JobTitleRules"
                        placeholder="Enter job title"
                        dense
                        outlined
                        required
                        prepend-inner-icon="mdi-badge-account-horizontal"
                      ></v-text-field>
                    </v-col>

                    <!-- Medical Information -->
                    <v-col cols="12">
                      <v-divider class="my-4"></v-divider>
                      <div class="section-subtitle-jci mb-3">
                        <v-icon small color="#EDBE38" class="mr-2"
                          >mdi-medical-bag</v-icon
                        >
                        <span class="font-weight-bold text-subtitle-2" style="color: #0A0F29;"
                          >Medical Information</span
                        >
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <label class="field-label">
                        <span class="error--text">*</span> Any known Allergies
                        to be declared
                      </label>
                      <v-textarea
                        v-model="Allergies"
                        :rules="AllergiesRules"
                        placeholder="Enter any known allergies (e.g., food, medication, environmental)"
                        dense
                        outlined
                        required
                        rows="3"
                        prepend-inner-icon="mdi-alert-circle-outline"
                        hint="Please list all allergies for your safety"
                        persistent-hint
                      ></v-textarea>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <label class="field-label">
                        <span class="error--text">*</span> Pre-Existing Medical
                        Conditions
                      </label>
                      <v-textarea
                        v-model="MedicalConditions"
                        :rules="MedicalConditionsRules"
                        placeholder="Enter any pre-existing conditions (e.g., Asthma, Hypertension, Diabetes)"
                        dense
                        outlined
                        required
                        rows="3"
                        prepend-inner-icon="mdi-heart-pulse"
                        hint="Please list conditions like Asthma, Hypertension, Diabetes, etc."
                        persistent-hint
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Document Details Card -->
              <v-card class="info-card-jci mb-6 animate-slide-up" elevation="3" rounded="lg">
                <v-card-title class="card-header-jci-aqua white--text">
                  <v-icon left color="white">mdi-file-document-multiple</v-icon>
                  <div>
                    <div>Passport Details</div>
                    <div class="text-caption font-weight-regular mt-1" style="opacity: 0.95; line-height: 1.4;">
                      <v-icon small color="white" class="mr-1">mdi-asterisk</v-icon>
                      Passport attachment is mandatory
                    </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="#EDBE38"
                    elevation="4"
                    class="jci-add-doc-btn"
                    @click.prevent="addDocumentPrompt = true"
                  >
                    <v-icon left color="#0A0F29">mdi-plus-circle</v-icon>
                    <span style="color: #0A0F29; font-weight: 700;">Add Document</span>
                  </v-btn>
                </v-card-title>
                <v-card-text class="pa-6">
                  <!-- Important Passport Validity Note -->
                  <v-alert
                    border="left"
                    colored-border
                    color="#EDBE38"
                    elevation="1"
                    class="mb-4"
                    dense
                  >
                    <div class="d-flex align-center">
                      <v-icon color="#EDBE38" class="mr-2">mdi-information-outline</v-icon>
                      <span style="color: #0A0F29; font-weight: 500;">
                        <strong>Important:</strong> Your passport must be valid for at least six months from the event date.
                      </span>
                    </div>
                  </v-alert>

                  <!-- Loading State -->
                  <div v-if="DocumentLoadingFlag" class="text-center py-4">
                    <v-progress-circular
                      indeterminate
                      color="#0097D7"
                      size="40"
                    ></v-progress-circular>
                  </div>

                  <!-- No Documents -->
                  <v-alert
                    v-if="!DocumentLoadingFlag && DocumentData.length === 0"
                    border="left"
                    colored-border
                    color="#EDBE38"
                    elevation="1"
                  >
                    <div class="d-flex align-center">
                      <v-icon class="mr-2" color="#EDBE38">mdi-alert-circle-outline</v-icon>
                      <span style="color: #0A0F29;"
                        >No documents added yet. Please add your documents
                        (Passport, ID proof, etc.)</span
                      >
                    </div>
                  </v-alert>

                  <!-- Document List -->
                  <v-container
                    class="py-0"
                    v-if="!DocumentLoadingFlag && DocumentData.length > 0"
                  >
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                        lg="4"
                        v-for="(item, index) in DocumentData"
                        :key="index"
                      >
                        <v-card class="document-card-jci animate-hover" outlined hover>
                          <v-card-text class="pa-3">
                            <div class="d-flex align-start">
                              <v-avatar size="70" tile rounded class="mr-3">
                                <v-img
                                  :src="item.ImagePath"
                                  :alt="item.DocumentName"
                                  contain
                                >
                                  <template v-slot:placeholder>
                                    <v-row
                                      class="fill-height ma-0"
                                      align="center"
                                      justify="center"
                                    >
                                      <v-icon>mdi-file-image</v-icon>
                                    </v-row>
                                  </template>
                                </v-img>
                              </v-avatar>
                              <div class="flex-grow-1">
                                <div
                                  class="d-flex align-center justify-space-between mb-1"
                                >
                                  <h4 class="document-title mb-0">
                                    {{ item.DocumentName }}
                                  </h4>
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-btn
                                        icon
                                        x-small
                                        color="error"
                                        @click="deleteDocumentAlert(item)"
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                        <v-icon small>mdi-delete</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>Delete Document</span>
                                  </v-tooltip>
                                </div>
                                <div class="text-caption text--secondary mb-1">
                                  {{ item.ReferenceNo }}
                                </div>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>

              <!-- Plan Selection Dialog -->
              <v-dialog v-model="PreviewPlanFlag" max-width="600">
                <event-plan-details
                  :Plan="PreviewPlanDetails"
                  @hideDialog="hidePreviewPlanPopup($event)"
                  v-if="PreviewPlanFlag"
                ></event-plan-details>
              </v-dialog>

              <!-- Add Document Dialog -->
              <v-dialog v-model="addDocumentPrompt" persistent max-width="800">
                <add-new-document
                  pageDescription="Add New Document"
                  aspacFlag="true"
                  :addRecordPrompt="addDocumentPrompt"
                  :MemberId="
                    selectedMember.MemberId || Presidentmember.LomPresidentId
                  "
                  @hideAddRecordPrompt="handleDocumentAdded($event)"
                  v-if="addDocumentPrompt"
                ></add-new-document>
              </v-dialog>

              <!-- Pricing Plans Section -->
              <div class="plans-section-jci animate-fade-in">
                <div class="text-center mb-6">
                  <h2 class="section-title-jci">
                    <v-icon large color="#3A67B1" class="mr-2"
                      >mdi-ticket-percent</v-icon
                    >
                    <span style="color: #0A0F29;">Choose Your Plan</span>
                  </h2>
                  <p class="section-subtitle-jci" style="color: #414042;">
                    Select the best plan that fits your needs
                  </p>
                </div>

                <v-row justify="center">
                  <v-col
                    cols="12"
                    sm="6"
                    md="4"
                    v-for="(item, index) in Plans"
                    :key="index"
                  >
                    <div v-if="item.PlanVisibleFlag">
                      <!-- Popular/Recommended Plan -->
                      <div v-if="item.Recommended" class="plan-card-wrapper animate-scale-up">
                        <div class="plan-card-jci popular-plan-jci elevation-8">
                          <div class="popular-badge-jci">
                            <v-chip color="#56BDA3" text-color="white" small>
                              <v-icon left small>mdi-star</v-icon>
                              Most Popular
                            </v-chip>
                          </div>

                          <div class="plan-header-jci">
                            <h3 class="plan-title-jci white--text">
                              {{ item.PlanName }}
                            </h3>
                          </div>

                          <div class="plan-body-jci">
                            <div class="plan-price-jci">
                              <div class="price-amount-jci">
                                {{ item.TotalAmountTxt }}
                              </div>
                              <div class="price-period-jci">per head</div>
                            </div>

                            <v-divider class="mb-4"></v-divider>

                            <!-- Action Buttons -->
                            <div class="mt-auto">
                              <v-btn
                                color="#3A67B1"
                                text
                                block
                                large
                                class="mb-3 view-details-btn-jci"
                                @click.prevent="showPreviewPlanPopup(item)"
                              >
                                <v-icon left size="20" color="#3A67B1"
                                  >mdi-information-outline</v-icon
                                >
                                <span style="color: #3A67B1;">View Details</span>
                              </v-btn>

                              <v-btn
                                color="#3A67B1"
                                block
                                x-large
                                elevation="2"
                                :disabled="!valid1"
                                :loading="SubmitFlag"
                                @click.prevent="confirmAlert(item)"
                                class="select-plan-btn-jci jci-btn-navy"
                              >
                                <v-icon left size="22" color="white"
                                  >mdi-check-circle-outline</v-icon
                                >
                                <span style="color: white;">Select This Plan</span>
                              </v-btn>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Regular Plan -->
                      <div v-else class="plan-card-wrapper animate-scale-up">
                        <div class="plan-card-jci elevation-4">
                          <div class="plan-header-jci-aqua">
                            <h3 class="plan-title-jci white--text">
                              {{ item.PlanName }}
                            </h3>
                            <p
                              class="plan-subtitle-jci white--text"
                              v-if="item.PlanDescription"
                            >
                              {{ item.PlanDescription }}
                            </p>
                          </div>

                          <div class="plan-body-jci">
                            <div class="plan-price-jci">
                              <div class="price-amount-jci" style="color: #0097D7;">
                                {{ item.TotalAmountTxt }}
                              </div>
                              <div class="price-period-jci">per head</div>
                            </div>

                            <v-divider class="mb-4"></v-divider>

                            <!-- Action Buttons -->
                            <div class="mt-auto">
                              <v-btn
                                color="#0097D7"
                                text
                                block
                                large
                                class="mb-3 view-details-btn-jci"
                                @click.prevent="showPreviewPlanPopup(item)"
                              >
                                <v-icon left size="20" color="#0097D7"
                                  >mdi-information-outline</v-icon
                                >
                                <span style="color: #0097D7;">View Details</span>
                              </v-btn>

                              <v-btn
                                color="#0097D7"
                                block
                                x-large
                                elevation="2"
                                :disabled="!valid1"
                                :loading="SubmitFlag"
                                @click.prevent="confirmAlert(item)"
                                class="select-plan-btn-jci jci-btn-aqua"
                              >
                                <v-icon left size="22" color="white"
                                  >mdi-check-circle-outline</v-icon
                                >
                                <span style="color: white;">Select This Plan</span>
                              </v-btn>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-form>
          </v-container>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import EventPlanDetails from "@/view/pages/erp/events/natcon/EventPlanDetails.vue";
import AddNewDocument from "@/view/pages/profile/my-profile/AddNewDocument";

export default {
  mixins: [common],
  components: {
    EventPlanDetails,
    AddNewDocument,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      ProgressLoadingFlag: false,
      SubmitFlag: false,
      SearchFlag: false,
      NotesFlag: false,
      rows: [],

      RegistrationTypeFlag: false,
      ParticipantShowFlag: false,
      RefreshButtonFlag: false,

      PreviewPlanFlag: false,
      PreviewPlanDetails: {},

      addDocumentPrompt: false,
      DocumentLoadingFlag: false,
      DocumentData: [],
      selectedDocument: {},

      tableColumns1: [],
      tableColumns2: [
        { text: "Date", value: "PaidDateTxt", width: "13%" },
        { text: "Description", value: "Description", width: "35%" },
        { text: "Late Fee", value: "FineAmountTxt", width: "13%" },
        { text: "To be paid", value: "FineNotPaid", width: "13%" },
        { text: "No of members", value: "Qty", width: "13%" },
        { text: "Sub Total", value: "SubTotal", width: "13%" },
      ],

      selected: [],
      search: "",

      RegistrationTypeId: "",
      RegistrationTypeIdRules: [(v) => !!v || "Registor for is required"],
      RegistrationTypeIdOptions: [
        { value: "", text: "--Select Type--" },
        { value: 1, text: "Chief delegate" },
        { value: 2, text: "Alternate chief delegate" },
        { value: 3, text: "Member" },
      ],

      Gender: "",
      GenderRules: [(v) => !!v || "Gender is required"],
      GenderOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Male" },
        { value: 2, text: "Female" },
        { value: 3, text: "Other" },
      ],

      FoodPreference: "",
      FoodPreferenceRules: [(v) => !!v || "Food preference is required"],
      FoodPreferenceOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Veg" },
        { value: 2, text: "Non Veg" },
      ],

      Lom: {},

      Presidentmember: {},
      Plans: [],

      selectedPlan: {},
      selectedMember: {},

      Events: {},

      MemberIdRules: [(v) => !!v || "Member is required"],
      MemberId: "",
      MemberIdOptions: [],
      MemberIdOptionsLoading: false,

      CheckoutTableHeaders: [],

      tab: null,
      TabDisable1: false,
      TabDisable2: false,

      TotalAmount: 0,
      Contribution: [],

      TotalAmount1: 0,
      TotalAmount2: 0,

      Member: {},
      Order: {},
      MemberImageFlag: false,

      PenaltySubscription: [],

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      OrderType: 1,

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      MobileNoRules: [(v) => !!v || "Mobile No is required"],

      EmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      QtyRules: [
        (v) => Number.isInteger(Number(v)) || "Nos is invalid",
        (v) => v >= 0 || "Number must be greater than zero",
      ],

      GstFlag: false,
      GstNumberFlag: 2,
      GstNumberFlagRules: [
        (v) => !!v || "Does your LOM has GST number field required",
      ],
      GstNumberFlagOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Yes" },
        { value: 2, text: "No" },
      ],

      GstNumber: "",
      GstNumberRules: [(v) => !!v || "GST number is required"],

      TshirtSize: "",
      TshirtSizeRules: [(v) => !!v || "T-Shirt size is required"],
      TshirtSizeOptions: [
        { value: "", text: "--Select--" },
        { value: "S", text: "S" },
        { value: "M", text: "M" },
        { value: "L", text: "L" },
        { value: "XL", text: "XL" },
        { value: "XXL", text: "XXL" },
        { value: "3XL", text: "3XL" },
        { value: "4XL", text: "4XL" },
        { value: "5XL", text: "5XL" },
      ],

      SenatorNumber: "",
      SenatorNumberRules: [],

      Gender: "",
      GenderRules: [(v) => !!v || "Gender is required"],
      GenderOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Male" },
        { value: 2, text: "Female" },
        { value: 3, text: "Other" },
      ],

      FoodPreference: "",
      FoodPreferenceRules: [(v) => !!v || "Food preference is required"],
      FoodPreferenceOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Vegan (Veg)" },
        { value: 2, text: "Non Veg" },
      ],

      LomCode: "",
      MemberTypeId: 1,
      EventId: 10,
      EventTypeId: 15,

      CompanyName: "",
      CompanyNameRules: [(v) => !!v || "Company/Organization name is required"],

      Department: "",
      DepartmentRules: [
        (v) => !!v || "Section/Division/Department is required",
      ],

      JobTitle: "",
      JobTitleRules: [(v) => !!v || "Job title is required"],

      Allergies: "",
      AllergiesRules: [
        (v) => !!v || "Please declare any known allergies or write 'None'",
      ],

      MedicalConditions: "",
      MedicalConditionsRules: [
        (v) => !!v || "Please declare any medical conditions or write 'None'",
      ],

      Order: {
        back_url: "",
        customer_name: "",
        customer_email_id: "",
        customer_mobile_no: "",
        rzp_action: "#",
        rzp_account_id: "",
        rzp_key: "bdgzbd",
        rzp_amount: "",
        rzp_order_id: "",
        rzp_buttontext: "Pay now",
        rzp_name: "JCI India Foundation",
        rzp_description: "",
        rzp_image: "",
        rzp_color: "#0096d5",
      },

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log("ZoneCode=" + ZoneCode);
        this.ZoneCode = ZoneCode;

        this.pageData();
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
    MemberIdOptions: function () {
      this.MemberIdOptionsLoading = false;
    },
    RegistrationTypeId: function () {
      console.log("watch RegistrationTypeId");
      this.selectedMember = {};
      var RegistrationTypeId = this.RegistrationTypeId;
      console.log("RegistrationTypeId=" + RegistrationTypeId);
      if (RegistrationTypeId != "") {
        if (RegistrationTypeId == 1) {
          var Presidentmember = this.Presidentmember;
          console.log("Presidentmember=" + JSON.stringify(Presidentmember));
          this.selectedMember = {
            MemberId: Presidentmember.LomPresidentMemberId,
            MemberName: Presidentmember.LomPresidentFullName,
            MembershipId: Presidentmember.LomPresidentMembershipId,
            MobileNo: Presidentmember.LomPresidentMobileNo,
            EmailId: Presidentmember.LomPresidentEmailid,
            Gender: Presidentmember.LomPresidentGender || "",
            FoodChoice: Presidentmember.LomPresidentFoodChoice || "",
            GstNumberFlag: 2,
            GstNumber: "",
            MemberImagePath: Presidentmember.MemberImagePath,
          };
        } else if (RegistrationTypeId >= 2) {
          this.getMemberDetails();
        } else {
        }
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var id = this.$route.query.id;
      console.log({ id });
      this.EventId = id;
      var url1 = "api/jci-program/show";
      var condition1 = {
        UserInterface: 1,
        Program: id,
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getPresidentMemberDetails();
      this.getEventsDetails();
      this.getMemberIdOptions();
      this.getDocumentDetails();
    },
    showPreviewPlanPopup(tr) {
      this.PreviewPlanDetails = tr;
      this.PreviewPlanFlag = true;
    },
    hidePreviewPlanPopup() {
      this.PreviewPlanDetails = {};
      this.PreviewPlanFlag = false;
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/staff/list";
      var params = {};
      this.pageRedirect(params, url);
    },
    resetForm() {
      this.$refs.form2.reset();
      this.selectedMember = {};
      this.selectedPlan = {};
      // this.refreshPageData();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    getMemberIdOptions() {
      console.log("getMemberIdOptions called");
      this.MemberIdOptionsLoading = true;
      var selectbox1_source = "MemberId";
      var selectbox1_destination = "MemberIdOptions";
      var selectbox1_url = "api/members/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
        LomCode: this.LomCode,
        MemberType: this.MemberTypeId,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getMemberDetails() {
      console.log("getMemberDetails called");
      this.selectedMember = {};
      var temp_code = this.MemberId;
      console.log("temp_code=" + temp_code);
      var idx = this.MemberIdOptions.map((e) => e.value).indexOf(temp_code);
      console.log("idx=" + idx);
      if (idx >= 0) {
        var member = this.MemberIdOptions[idx];
        console.log("member=" + JSON.stringify(member));
        this.selectedMember = {
          MemberId: member.value,
          MemberName: member.member_name,
          MembershipId: member.membership_id,
          MobileNo: member.mobile_no,
          EmailId: member.email_id,
          Gender: member.gender || "",
          FoodChoice: member.food_choice || "",
          GstNumberFlag: 2,
          GstNumber: "",
          MemberImagePath: member.photo,
        };
        this.getDocumentDetails();
      }
    },
    getDocumentDetails() {
      console.log("getDocumentDetails called");

      var presidentMember = this.Presidentmember;
      console.log({ presidentMember });

      var presidentMemberId = presidentMember.LomPresidentMemberId || "";
      console.log({ presidentMemberId });

      var memberId = this.selectedMember.MemberId;
      console.log({ memberId });

      var MemberId =
        memberId > 0
          ? memberId
          : presidentMemberId > 0
          ? presidentMemberId
          : "";
      console.log({ MemberId });

      if (MemberId == "") {
        console.log("No MemberId available");
        return;
      }

      this.DocumentLoadingFlag = true;
      this.DocumentData = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/members-document-details/show";
      var upload = {
        UserInterface: 2,
        MemberId: MemberId,
      };

      console.log("upload=" + JSON.stringify(upload));

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.DocumentLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);

          if (flag == 1) {
            thisIns.DocumentData = records.TableData || [];
            console.log(thisIns.DocumentData);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.DocumentLoadingFlag = false;
        });
    },
    handleDocumentAdded(flag) {
      console.log("handleDocumentAdded called, flag=" + flag);
      this.addDocumentPrompt = false;
      if (flag) {
        this.getDocumentDetails();
        this.toast("success", "Document added successfully");
      }
    },
    deleteDocumentAlert(tr) {
      console.log("deleteDocumentAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedDocument = tr;
        this.confirmDeleteDocument();
      } else {
        this.toast("error", "Kindly select one document to delete");
      }
    },
    confirmDeleteDocument() {
      console.log("confirmDeleteDocument called");
      var tr = this.selectedDocument;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "Document name <b> " + tr.DocumentName + "</b>";
        htmlTxt += "<br/> Reference No <b> " + tr.ReferenceNo + "</b>";
        htmlTxt +=
          "<br/><img width='100' height='100' src='" + tr.ImagePath + "' />";
        htmlTxt += "<br/><br/>Do you want to delete this document?";
        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete this document?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteDocument();
          } else {
            this.toast("error", "You cancelled the process");
          }
        });
      } else {
        this.toast("error", "Kindly select one document to delete");
      }
    },
    deleteDocument() {
      console.log("deleteDocument called");

      var tr = this.selectedDocument;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.DocumentLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members-document-details/destroy";
        var upload = {
          UserInterface: 1,
          Member: tr.MemberId,
          Document: tr.DocumentId,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.DocumentLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.selectedDocument = {};
              thisIns.getDocumentDetails();
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.DocumentLoadingFlag = false;
          });
      } else {
        this.toast("error", "Kindly select one document to delete");
      }
    },
    getPresidentMemberDetails() {
      console.log("getPresidentMemberDetails is called");

      var LomId = this.LomCode;
      console.log("LomId=" + LomId);

      if (LomId != "") {
        this.SubmitFlag = true;
        this.StartupLoadingFlag = true;
        this.Presidentmember = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/lom/show";
        var upload = {
          UserInterface: 2,
          Lom: LomId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Lom = records;
              thisIns.Presidentmember = records.President;
              thisIns.RegistrationTypeId = 1;
              // thisIns.toast("success", output);
              // thisIns.resetForm();
              thisIns.getDocumentDetails();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;
          });
      } else {
        var message = "";
        if (LomId == "") {
          message += "Your session is expired. Kindly login again. ";
        }
        this.toast("error", message);
      }
    },
    getEventsDetails() {
      console.log("getEventsDetails is called");

      var EventId = this.EventId;
      console.log("EventId=" + EventId);

      if (EventId != "") {
        this.SubmitFlag = true;
        this.StartupLoadingFlag = true;

        this.Plans = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/plan/lists";
        var upload = {
          UserInterface: 2,
          Program: EventId,
          ProgramType: this.EventTypeId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Plans = records.TableData;
              // thisIns.toast("success", output);
              // thisIns.resetForm();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;
          });
      } else {
        var message = "";
        if (EventId == "") {
          message += "Event id should not be empty";
        }
        this.toast("error", message);
      }
    },
    confirmAlert(selectedPlan) {
      var validate1 = this.$refs.form2.validate();
      this.selectedPlan = selectedPlan;
      var selectedMember = this.selectedMember;
      console.log(
        "validate1=" +
          validate1 +
          ", selectedPlan=" +
          JSON.stringify(selectedPlan) +
          ", selectedMember=" +
          JSON.stringify(selectedMember)
      );

      var MemberId = selectedMember.MemberId;
      MemberId = MemberId == (null || undefined) ? "" : MemberId;
      console.log("MemberId=" + MemberId);

      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log("EventPlanId=" + EventPlanId);

      if (validate1 && MemberId != "" && EventPlanId != "") {
        var htmlTxt = "";
        // htmlTxt += "<b>Please verify all the information before proceeding</b>";
        htmlTxt += "<b>Selected member details</b>";
        htmlTxt += "<br/>Member Name <b>" + selectedMember.MemberName + "</b>";
        htmlTxt += "<br/>Mobile no. <b>" + selectedMember.MobileNo + "</b>";
        htmlTxt += "<br/>Email id <b>" + selectedMember.EmailId + "</b>";
        htmlTxt +=
          "<br/><img width='100' height='100' src='" +
          selectedMember.MemberImagePath +
          "' />";
        htmlTxt += "<br/><b>Selected plan details</b>";
        htmlTxt += "<br/>Plan name <b>" + selectedPlan.PlanName + "</b>";
        htmlTxt +=
          "<br/>Total amount <b>" + selectedPlan.TotalAmountTxt + "</b>";
        htmlTxt += "<br/><br/>Do you want to continue?";
        Swal.fire({
          title: "Please verify all the information before proceeding",
          text: "",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
            // this.makePayment();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (MemberId == "") {
          message += "Kindly select member. ";
        }
        if (EventPlanId == "") {
          message += "Kindly select plan. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form2.validate();
      var selectedPlan = this.selectedPlan;
      var selectedMember = this.selectedMember;
      console.log(
        "validate1=" +
          validate1 +
          ", selectedPlan=" +
          JSON.stringify(selectedPlan) +
          ", selectedMember=" +
          JSON.stringify(selectedMember)
      );

      var MemberId = selectedMember.MemberId;
      MemberId = MemberId == (null || undefined) ? "" : MemberId;
      console.log("MemberId=" + MemberId);

      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log("EventPlanId=" + EventPlanId);

      var participants = [];
      participants.push(selectedMember);

      var TotalCount = participants.length;
      console.log("TotalCount=" + TotalCount);

      var EventTotalAmount = selectedPlan.TotalAmount;
      console.log("EventTotalAmount=" + EventTotalAmount);

      var TotalAmount = parseFloat(TotalCount) * parseFloat(EventTotalAmount);
      console.log("TotalAmount=" + TotalAmount);

      if (validate1 && TotalCount > 0 && TotalAmount > 0) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/order/create";
        var upload = {
          UserInterface: 1,
          RegistrationType: this.RegistrationTypeId,
          Lom: this.LomCode,
          Member: MemberId,
          Event: this.EventId,
          Plan: EventPlanId,
          MobileNo: selectedMember.MobileNo,
          EmailId: selectedMember.EmailId,
          Gender: selectedMember.Gender,
          FoodPreference: selectedMember.FoodChoice,
          GstNumberFlag: selectedMember.GstNumberFlag,
          GstNumber:
            selectedMember.GstNumberFlag == 1 ? selectedMember.GstNumber : null,
          TshirtSize: this.TshirtSize,
          PassportNumber: this.PassportNumber,
          SenatorNumber: this.SenatorNumber,
          CompanyName: this.CompanyName,
          Department: this.Department,
          JobTitle: this.JobTitle,
          Allergies: this.Allergies,
          MedicalConditions: this.MedicalConditions,
          TotalCount: TotalCount,
          TotalAmount: TotalAmount,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.SubmitFlag = true;
        this.ProgressLoadingFlag = true;

        // this.$session.set("checkout", upload);
        // this.checkout();

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.ProgressLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              /*
              thisIns.sweetAlert(
                "info",
                "You will be redirected to checkout page shortly",
                true
              );
              */
              thisIns.Order = records.Order;
              thisIns.makePayment();
            } else {
              // thisIns.toast("error", output);
              // thisIns.Order = records.Order;
              // thisIns.makePayment();
              thisIns.sweetAlert("info", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.ProgressLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (TotalCount == 0) {
          message += "Kindly select member. ";
        }
        if (TotalAmount == 0) {
          message += "Kindly select a plan. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    makePayment() {
      console.log("makePayment");
      // ev.preventDefault();
      var order = this.Order;
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        // callback_url: "http://domain.com/#/about/" + this.order_id,
        redirect: false,
        handler: function (response) {
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.SubmitFlag = true;
          // thisIns.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
          thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },
  },
  beforeMount() {
    this.refreshPageData();
    /*
    this.tableColumns1.push(
      { text: "Description", value: "Description" },
      { text: "Subscription Fees", value: "AmountTxt" },
      { text: "Nos", value: "Qty" },
      { text: "Sub Total", value: "SubTotal" }
      // { text: "Actions", value: "actions" }
    );
    */
  },
};
</script>

<style lang="scss" scoped>
// JCI Brand Colors
$jci-aqua: #0097D7;
$jci-navy: #3A67B1;
$jci-gold: #EDBE38;
$jci-seafoam: #56BDA3;
$jci-black-90: #414042;
$jci-black-off: #0A0F29;
$jci-white: #ffffff;

#natcon-payment-create {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
  padding: 20px 0;

  // Hero Section with JCI Branding
  .hero-card {
    background: transparent !important;
    margin-bottom: 30px;
  }

  .hero-gradient-jci {
    background: linear-gradient(135deg, $jci-navy 0%, $jci-aqua 100%);
    border-radius: 20px;
    box-shadow: 0 12px 48px rgba(58, 103, 177, 0.35);
    position: relative;
    overflow: hidden;
    padding: 0;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.12) 1px,
        transparent 1px
      );
      background-size: 50px 50px;
      animation: backgroundMove 20s linear infinite;
    }
  }

  @keyframes backgroundMove {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(50px, 50px);
    }
  }

  .hero-icon-box-jci {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 18px;
    padding: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(12px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(237, 190, 56, 0.2) 0%, rgba(0, 151, 215, 0.2) 100%);
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    &:hover {
      transform: scale(1.08) rotate(2deg);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);

      &::before {
        opacity: 1;
      }

      .hero-icon-animated {
        animation: iconPulse 0.6s ease-in-out;
      }
    }
  }

  // Hero Icon Animation
  .hero-icon-animated {
    animation: iconFloat 3s ease-in-out infinite, iconGlow 2s ease-in-out infinite;
    filter: drop-shadow(0 0 8px rgba(237, 190, 56, 0.4));
  }

  @keyframes iconFloat {
    0%, 100% {
      transform: translateY(0) rotate(0deg);
    }
    25% {
      transform: translateY(-8px) rotate(-5deg);
    }
    50% {
      transform: translateY(0) rotate(0deg);
    }
    75% {
      transform: translateY(-4px) rotate(3deg);
    }
  }

  @keyframes iconGlow {
    0%, 100% {
      filter: drop-shadow(0 0 8px rgba(237, 190, 56, 0.4));
    }
    50% {
      filter: drop-shadow(0 0 16px rgba(237, 190, 56, 0.8)) drop-shadow(0 0 24px rgba(0, 151, 215, 0.4));
    }
  }

  @keyframes iconPulse {
    0%, 100% {
      transform: scale(1) rotate(0deg);
    }
    25% {
      transform: scale(1.15) rotate(-10deg);
    }
    75% {
      transform: scale(1.15) rotate(10deg);
    }
  }

  .hero-title-jci {
    font-size: 2rem;
    font-weight: 800;
    text-shadow: 0 3px 12px rgba(0, 0, 0, 0.2);
    letter-spacing: -0.5px;
    line-height: 1.2;
  }

  .hero-subtitle-jci {
    font-size: 1.05rem;
    font-weight: 400;
    opacity: 0.95;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    line-height: 1.5;
  }

  .hero-action-btn-jci {
    font-weight: 700;
    letter-spacing: 0.5px;
    border-radius: 12px !important;
    padding: 14px 36px !important;
    height: 52px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(237, 190, 56, 0.4);

    &:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 8px 24px rgba(237, 190, 56, 0.5);
    }
  }

  // JCI Alert Styling
  .jci-alert {
    border-radius: 12px;
    animation: slideInDown 0.5s ease-out;
  }

  // Main Card with JCI Styling
  .main-card-jci {
    background: white;
    border-radius: 18px;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12);
    border: 1px solid rgba(58, 103, 177, 0.1);
  }

  .required-note-jci {
    display: flex;
    align-items: center;
    color: $jci-black-90;
    font-size: 0.875rem;
    padding: 8px 12px;
    background: rgba(237, 190, 56, 0.08);
    border-radius: 8px;
  }

  // Info Cards with JCI Colors
  .info-card-jci {
    border-radius: 14px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;
    background: white;

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 18px 45px rgba(0, 0, 0, 0.18);
      border-color: rgba(0, 151, 215, 0.3);
    }

    .card-header-jci-navy {
      padding: 18px 24px;
      border-radius: 14px 14px 0 0;
      font-size: 1.15rem;
      background: linear-gradient(135deg, $jci-navy 0%, darken($jci-navy, 8%) 100%);
      box-shadow: 0 4px 12px rgba(58, 103, 177, 0.25);
    }

    .card-header-jci-aqua {
      padding: 18px 24px;
      border-radius: 14px 14px 0 0;
      font-size: 1.15rem;
      background: linear-gradient(135deg, $jci-aqua 0%, darken($jci-aqua, 8%) 100%);
      box-shadow: 0 4px 12px rgba(0, 151, 215, 0.25);
    }

    .card-header-jci-seafoam {
      padding: 18px 24px;
      border-radius: 14px 14px 0 0;
      font-size: 1.15rem;
      background: linear-gradient(135deg, $jci-seafoam 0%, darken($jci-seafoam, 8%) 100%);
      box-shadow: 0 4px 12px rgba(86, 189, 163, 0.25);
    }

    .card-header-jci-gold {
      padding: 18px 24px;
      border-radius: 14px 14px 0 0;
      font-size: 1.15rem;
      background: linear-gradient(135deg, $jci-gold 0%, darken($jci-gold, 5%) 100%);
      box-shadow: 0 4px 12px rgba(237, 190, 56, 0.3);
    }
  }

  // JCI Button Styles
  .jci-btn {
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    }

    &.jci-btn-aqua {
      background: linear-gradient(135deg, $jci-aqua 0%, darken($jci-aqua, 5%) 100%) !important;
    }

    &.jci-btn-navy {
      background: linear-gradient(135deg, $jci-navy 0%, darken($jci-navy, 5%) 100%) !important;
    }

    &.jci-btn-seafoam {
      background: linear-gradient(135deg, $jci-seafoam 0%, darken($jci-seafoam, 5%) 100%) !important;
    }

    &.jci-btn-dark {
      background: linear-gradient(135deg, $jci-black-90 0%, $jci-black-off 100%) !important;
    }
  }

  .jci-btn-text-white {
    &:hover {
      background-color: rgba(255, 255, 255, 0.15) !important;
    }
  }

  // Add Document Button - Distinct JCI Gold styling
  .jci-add-doc-btn {
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: none;
    border-radius: 10px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(237, 190, 56, 0.5) !important;
    padding: 8px 24px !important;

    &:hover {
      transform: translateY(-2px) scale(1.03);
      box-shadow: 0 8px 24px rgba(237, 190, 56, 0.6) !important;
      background-color: darken(#EDBE38, 5%) !important;
    }

    .v-icon {
      transition: transform 0.3s ease;
    }

    &:hover .v-icon {
      transform: rotate(90deg) scale(1.1);
    }
  }

  // Section Subtitles
  .section-subtitle-jci {
    display: flex;
    align-items: center;
    color: $jci-black-off;
    padding: 10px 14px;
    background: rgba(0, 151, 215, 0.05);
    border-radius: 10px;
    border-left: 4px solid $jci-aqua;
  }

  // Document Card JCI
  .document-card-jci {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 14px !important;
    border: 2px solid rgba(0, 151, 215, 0.1);

    &:hover {
      transform: translateY(-6px) scale(1.02);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18) !important;
      border-color: $jci-aqua;
    }
  }

  .document-title {
    font-size: 14px;
    font-weight: 600;
    color: $jci-black-off;
    line-height: 1.4;
  }

  // Plans Section JCI
  .plans-section-jci {
    margin-top: 48px;
    padding: 24px 0;
  }

  .section-title-jci {
    font-size: 2.5rem;
    font-weight: 800;
    color: $jci-black-off;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.2;
  }

  .section-subtitle-jci {
    font-size: 1.1rem;
    color: $jci-black-90;
    margin-bottom: 0;
  }

  .plan-card-wrapper {
    height: 100%;
    margin-bottom: 24px;
  }

  .plan-card-jci {
    background: white;
    border-radius: 18px;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    height: 100%;
    min-height: 480px;
    display: flex;
    flex-direction: column;
    border: 3px solid transparent;
    position: relative;

    &:hover {
      transform: translateY(-12px) scale(1.02);
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
    }

    &.popular-plan-jci {
      border-color: $jci-seafoam;
      box-shadow: 0 18px 54px rgba(86, 189, 163, 0.35);
    }

    .popular-badge-jci {
      position: absolute;
      top: 18px;
      right: 18px;
      z-index: 10;
      animation: bounceIn 1s ease-out, pulse 2s infinite 1s;
    }

    @keyframes bounceIn {
      0% {
        transform: scale(0);
        opacity: 0;
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }

    @keyframes pulse {
      0%,
      100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.06);
      }
    }

    .plan-header-jci {
      padding: 32px 24px;
      text-align: center;
      background: linear-gradient(135deg, $jci-navy 0%, darken($jci-navy, 10%) 100%);
      box-shadow: 0 6px 18px rgba(58, 103, 177, 0.3);
    }

    .plan-header-jci-aqua {
      padding: 32px 24px;
      text-align: center;
      background: linear-gradient(135deg, $jci-aqua 0%, darken($jci-aqua, 10%) 100%);
      box-shadow: 0 6px 18px rgba(0, 151, 215, 0.3);
    }

    .plan-title-jci {
      font-size: 1.85rem;
      font-weight: 800;
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 1.2px;
    }

    .plan-subtitle-jci {
      font-size: 0.95rem;
      margin: 10px 0 0;
      opacity: 0.92;
    }

    .plan-body-jci {
      padding: 36px 24px;
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .plan-price-jci {
      text-align: center;
      margin-bottom: 24px;
    }

    .price-amount-jci {
      font-size: 3.2rem;
      font-weight: 900;
      line-height: 1;
      margin-bottom: 10px;
      background: linear-gradient(135deg, $jci-navy 0%, $jci-aqua 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .price-period-jci {
      font-size: 0.95rem;
      color: $jci-black-90;
      text-transform: uppercase;
      letter-spacing: 1.2px;
      font-weight: 600;
    }

    // JCI Button Styles for Plans
    .view-details-btn-jci {
      font-weight: 600;
      letter-spacing: 0.5px;
      text-transform: none;
      border-radius: 12px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      padding: 14px 28px !important;
      height: 52px !important;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
        transform: translateY(-2px);
      }

      .v-icon {
        transition: transform 0.3s ease;
      }

      &:hover .v-icon {
        transform: scale(1.15);
      }
    }

    .select-plan-btn-jci {
      font-weight: 700;
      letter-spacing: 0.6px;
      text-transform: none;
      border-radius: 14px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
      padding: 18px 36px !important;
      height: 60px !important;

      span {
        font-size: 1.05rem;
        letter-spacing: 0.3px;
      }

      &:hover {
        transform: translateY(-4px) scale(1.02);
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
      }

      &:active {
        transform: translateY(-2px) scale(1.01);
      }

      .v-icon {
        transition: transform 0.3s ease;
      }

      &:hover .v-icon {
        transform: scale(1.15) rotate(5deg);
      }
    }
  }

  // CSS Animations
  @keyframes slideInDown {
    from {
      transform: translateY(-30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .animate-fade-in {
    animation: fadeIn 0.6s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .animate-slide-right {
    animation: slideRight 0.6s ease-out;
  }

  @keyframes slideRight {
    from {
      transform: translateX(-30px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .animate-slide-left {
    animation: slideLeft 0.6s ease-out;
  }

  @keyframes slideLeft {
    from {
      transform: translateX(30px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .animate-slide-up {
    animation: slideUp 0.6s ease-out;
  }

  @keyframes slideUp {
    from {
      transform: translateY(30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .animate-scale-up {
    animation: scaleUp 0.5s ease-out;
  }

  @keyframes scaleUp {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .animate-hover {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-4px) scale(1.02);
    }
  }

  // Field Labels
  .field-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: $jci-black-90;
    font-size: 0.95rem;
  }

  // Responsive Design
  @media (max-width: 960px) {
    .hero-title-jci {
      font-size: 1.6rem;
    }

    .hero-subtitle-jci {
      font-size: 0.95rem;
    }

    .hero-icon-box-jci {
      padding: 18px;

      .v-icon {
        font-size: 52px !important;
      }
    }

    .hero-action-btn-jci {
      width: 100%;
      margin-top: 16px;
    }

    .section-title-jci {
      font-size: 2.2rem;
    }

    .plan-card-jci {
      min-height: auto;
    }

    .price-amount-jci {
      font-size: 2.8rem;
    }
  }

  @media (max-width: 600px) {
    .hero-title-jci {
      font-size: 1.4rem;
    }

    .hero-subtitle-jci {
      font-size: 0.9rem;
    }

    .hero-icon-box-jci {
      padding: 14px;
      margin-right: 12px !important;

      .v-icon {
        font-size: 44px !important;
      }
    }

    .section-title-jci {
      font-size: 1.85rem;
    }

    .plan-card-jci {
      .price-amount-jci {
        font-size: 2.4rem;
      }

      .plan-title-jci {
        font-size: 1.6rem;
      }
    }

    .jci-btn {
      padding: 12px 24px !important;
      font-size: 0.9rem !important;
    }

    .select-plan-btn-jci {
      height: 54px !important;
      padding: 16px 28px !important;

      span {
        font-size: 0.95rem;
      }
    }
  }

  // Fade transition
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>